import React from "react";
import { CENTRE, RINGS, SIZE, annularSector, polar, radius, spreadGlyphs } from "./lib/wheel";

// Rails sends finished longitudes; this draws them. There is no astronomy here
// and there must not be — the same JSON should later drive transits, synastry
// and returns without this file learning anything new.

// U+FE0E, the text variation selector, on every symbol. Without it the browser
// renders these through the emoji font — the zodiac comes out as purple
// rounded squares and Mars as a cartoon. The Tree hit the same thing with the
// alchemical element glyphs and solved it by falling back to the word; here the
// glyph IS the notation, so it is asked for as text instead.
const TEXT = "\uFE0E";

const SIGN_GLYPHS = ["♈", "♉", "♊", "♋", "♌", "♍", "♎", "♏", "♐", "♑", "♒", "♓"]
  .map((glyph) => glyph + TEXT);
const SIGN_ELEMENTS = ["fire", "earth", "air", "water"];

const PLANET_GLYPHS = Object.fromEntries(
  Object.entries({
    sun: "☉", moon: "☽", mercury: "☿", venus: "♀", mars: "♂", jupiter: "♃",
    saturn: "♄", uranus: "♅", neptune: "♆", pluto: "♇",
    true_node: "☊", mean_node: "☊", chiron: "⚷"
  }).map(([slug, glyph]) => [slug, glyph + TEXT])
);

const ANGLE_LABELS = {
  ascendant: "ASC",
  midheaven: "MC",
  descendant: "DSC",
  imumCoeli: "IC"
};

function ZodiacRing({ ascendant }) {
  return (
    <g className="wheel__zodiac" data-testid="zodiac-ring">
      <circle cx={CENTRE} cy={CENTRE} r={radius(RINGS.zodiacOuter)} className="wheel__ring" />
      <circle cx={CENTRE} cy={CENTRE} r={radius(RINGS.zodiacInner)} className="wheel__ring" />

      {SIGN_GLYPHS.map((glyph, index) => {
        const start = index * 30;
        const spoke = polar(start, ascendant, RINGS.zodiacOuter);
        const spokeInner = polar(start, ascendant, RINGS.zodiacInner);
        const label = polar(start + 15, ascendant, (RINGS.zodiacOuter + RINGS.zodiacInner) / 2);
        return (
          <g key={glyph} data-testid={`sign-${index}`}>
            <path
              className={`wheel__sign wheel__sign--${SIGN_ELEMENTS[index % 4]}`}
              d={annularSector(start, start + 30, ascendant, RINGS.zodiacOuter, RINGS.zodiacInner)}
            />
            <line
              className="wheel__spoke"
              x1={spoke.x} y1={spoke.y} x2={spokeInner.x} y2={spokeInner.y}
            />
            <text className="wheel__sign-glyph" x={label.x} y={label.y}
                  textAnchor="middle" dominantBaseline="central">
              {glyph}
            </text>
          </g>
        );
      })}
    </g>
  );
}

function HouseCusps({ cusps, ascendant }) {
  return (
    <g className="wheel__houses" data-testid="house-cusps">
      <circle cx={CENTRE} cy={CENTRE} r={radius(RINGS.houseInner)} className="wheel__ring" />
      {cusps.map((cusp, index) => {
        const outer = polar(cusp.longitude, ascendant, RINGS.zodiacInner);
        const inner = polar(cusp.longitude, ascendant, RINGS.houseInner);
        const next = cusps[(index + 1) % cusps.length].longitude;
        const midpoint = cusp.longitude + ((((next - cusp.longitude) % 360) + 360) % 360) / 2;
        const number = polar(midpoint, ascendant, RINGS.houseNumbers);
        return (
          <g key={cusp.house} data-testid={`cusp-${cusp.house}`}>
            <line className="wheel__cusp" x1={outer.x} y1={outer.y} x2={inner.x} y2={inner.y} />
            <text className="wheel__house-number" x={number.x} y={number.y}
                  textAnchor="middle" dominantBaseline="central">
              {cusp.house}
            </text>
          </g>
        );
      })}
    </g>
  );
}

function Angles({ angles }) {
  const ascendant = angles.ascendant.longitude;
  return (
    <g className="wheel__angles" data-testid="angles">
      {Object.entries(ANGLE_LABELS).map(([key, label]) => {
        const point = angles[key];
        if (!point) return null;
        const outer = polar(point.longitude, ascendant, 1.0);
        const inner = polar(point.longitude, ascendant, RINGS.houseInner);
        const text = polar(point.longitude, ascendant, 1.005);
        const onLeft = text.x < CENTRE;
        return (
          <g key={key} data-testid={`angle-${key}`}>
            <line
              className={`wheel__angle wheel__angle--${key}`}
              x1={outer.x} y1={outer.y} x2={inner.x} y2={inner.y}
            />
            <text
              className="wheel__angle-label"
              x={text.x} y={text.y}
              textAnchor={onLeft ? "end" : "start"}
              dominantBaseline="central"
            >
              {label}
            </text>
          </g>
        );
      })}
    </g>
  );
}

function AspectLines({ aspects, points, ascendant }) {
  const byslug = new Map(points.map((p) => [p.slug, p.longitude]));
  return (
    <g className="wheel__aspects" data-testid="aspect-lines">
      {aspects.map((aspect) => {
        const from = byslug.get(aspect.from);
        const to = byslug.get(aspect.to);
        if (from === undefined || to === undefined) return null;
        const a = polar(from, ascendant, RINGS.aspects);
        const b = polar(to, ascendant, RINGS.aspects);
        return (
          <line
            key={`${aspect.from}-${aspect.to}-${aspect.aspect}`}
            className={`wheel__aspect wheel__aspect--${aspect.aspect}`}
            x1={a.x} y1={a.y} x2={b.x} y2={b.y}
            data-testid={`aspect-${aspect.from}-${aspect.to}`}
          />
        );
      })}
    </g>
  );
}

function Planets({ planets, ascendant, onSelect, selected }) {
  // The tick stays at the true longitude; only the glyph moves.
  const spread = spreadGlyphs(planets);

  return (
    <g className="wheel__planets" data-testid="planets">
      {spread.map((planet) => {
        const glyphAt = polar(planet.display, ascendant, RINGS.planets);
        const tickOuter = polar(planet.longitude, ascendant, RINGS.planetTicks);
        const tickInner = polar(planet.longitude, ascendant, RINGS.planetTicks - 0.03);
        const isSelected = selected === planet.slug;
        return (
          <g
            key={planet.slug}
            className={isSelected ? "wheel__planet wheel__planet--on" : "wheel__planet"}
            role="button"
            tabIndex={0}
            aria-label={`${planet.slug}, ${planet.formatted}, house ${planet.house}${
              planet.retrograde ? ", retrograde" : ""
            }`}
            onClick={() => onSelect(isSelected ? null : planet.slug)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onSelect(isSelected ? null : planet.slug);
              }
            }}
            data-testid={`planet-${planet.slug}`}
            data-longitude={planet.longitude}
          >
            {/* Where it ACTUALLY is, whatever the glyph had to do to be read. */}
            <line className="wheel__planet-tick"
                  x1={tickOuter.x} y1={tickOuter.y} x2={tickInner.x} y2={tickInner.y} />
            <text className="wheel__planet-glyph" x={glyphAt.x} y={glyphAt.y}
                  textAnchor="middle" dominantBaseline="central">
              {PLANET_GLYPHS[planet.slug] || planet.slug.slice(0, 2)}
            </text>
            {planet.retrograde && (
              <text className="wheel__planet-retro" x={glyphAt.x + 18} y={glyphAt.y + 14}
                    textAnchor="middle">℞</text>
            )}
          </g>
        );
      })}
    </g>
  );
}

export default function ChartWheel({ chart, selected, onSelect }) {
  const ascendant = chart.angles.ascendant.longitude;
  const aspectPoints = [
    { slug: "ascendant", longitude: chart.angles.ascendant.longitude },
    { slug: "midheaven", longitude: chart.angles.midheaven.longitude },
    ...chart.planets.map((p) => ({ slug: p.slug, longitude: p.longitude }))
  ];

  return (
    <svg
      className="wheel"
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={
        `Natal chart wheel. Ascendant ${chart.angles.ascendant.formatted}, ` +
        `Midheaven ${chart.angles.midheaven.formatted}. ` +
        (chart.planets.length
          ? `${chart.planets.length} bodies.`
          : "No planetary positions are available.")
      }
      data-testid="chart-wheel"
    >
      <ZodiacRing ascendant={ascendant} />
      <HouseCusps cusps={chart.houses.cusps} ascendant={ascendant} />
      <AspectLines aspects={chart.aspects.list} points={aspectPoints} ascendant={ascendant} />
      <Angles angles={chart.angles} />
      <Planets
        planets={chart.planets}
        ascendant={ascendant}
        selected={selected}
        onSelect={onSelect}
      />
    </svg>
  );
}
